// users.router.js
//
// Три контекста по индустриальной практике (GitHub-стиль):
//   1. /users/:_id           — публичный read-only просмотр чужого профиля
//                              (контракт для community/organizations/etc. модулей).
//   2. /profile, /profile/*  — свой профиль/настройки/дашборд, БЕЗ ID в URL.
//                              Один компонент Profile/ProfileEdit/UserDashboard
//                              читает userId либо из route.params._id, либо
//                              из storeAuth.state.user._id (см. компоненты).
//   3. /backoffice/users/:_id/* — admin edit чужих юзеров. См. users.backoffice.router.js.
//
// edit/* и dashboard ИЗ-ПОД /users/:_id убраны: для чужого профиля настройки
// бессмысленны (своё → /profile/*, админское → /backoffice/users/:_id/*).

import * as auth from '@martyrs/src/modules/auth/views/store/auth.js';
import { useGlobalMixins } from '@martyrs/src/modules/core/views/mixins/mixins.js';
import { requiresAuth } from '../middlewares/auth.validation.js';
import { navigationItems } from '@martyrs/src/modules/auth/views/configs/navigation.user.config.js';
import HelpCard from '@martyrs/src/modules/core/views/components/blocks/HelpCard.vue';

const { isPhone, isTablet } = useGlobalMixins();

// Public read-only просмотр (`/users/:_id`).
// edit/* и dashboard здесь больше НЕ регистрируются — см. getProfileRoutes().
export function getUsersRoutes(options = {}) {
  const route = options.route || 'Home';

  return {
    parentName: route,
    config: {
      basePath: 'users',
      meta: {
        title: {
          en: 'Users',
          ru: 'Пользователи',
        },
      },
      routes: [
        {
          path: ':_id',
          name: 'User Profile Root',
          meta: {
            title: {
              en: 'Profile',
              ru: 'Профиль',
            },
            sidebar_hover: false,
            sidebar_navigation_items: navigationItems,
            sidebar_footer_component: HelpCard,
            sidebarOpenOnEnter: true,
            sidebarCloseOnLeave: true
          },
          children: [
            {
              path: '',
              name: 'User Profile',
              meta: {
                title: {
                  en: 'Profile',
                  ru: 'Профиль',
                },
                title_hide: true,
                hideNavigationBar: route => route.params._id === auth.state.user._id,
              },
              component: () => import(/* webpackChunkName: "profile" */ '../components/pages/Profile.vue'),
            },
          ],
        },
      ],
    }
  };
}

// Свой профиль (`/profile`, `/profile/edit/*`, `/profile/dashboard`).
// Те же компоненты, что и под :_id-схемой, но без ID в URL.
// Внутри компонентов userId = useRoute().params._id || storeAuth.state.user._id.
export function getProfileRoutes(options = {}) {
  const route = options.route || 'Home';

  return {
    parentName: route,
    config: {
      basePath: 'profile',
      meta: {
        title: {
          en: 'Profile',
          ru: 'Профиль',
        },
      },
      routes: [
        {
          path: '',
          name: 'My Profile',
          beforeEnter: [requiresAuth],
          meta: {
            title: {
              en: 'Profile',
              ru: 'Профиль',
            },
            sidebar_hover: false,
            sidebar_navigation_items: navigationItems,
            sidebar_footer_component: HelpCard,
            sidebarOpenOnEnter: true,
            sidebarCloseOnLeave: true,
            title_hide: true,
          },
          component: () => import(/* webpackChunkName: "profile" */ '../components/pages/Profile.vue'),
        },
        {
          path: 'edit',
          name: 'My Profile Edit',
          beforeEnter: [requiresAuth],
          meta: {
            title: {
              en: 'Profile Settings',
              ru: 'Настройки профиля',
            },
          },
          component: () => import(/* webpackChunkName: "profile-edit" */ '../components/pages/ProfileEdit.vue'),
          children: [
            {
              path: '',
              redirect: { name: 'My Profile Edit Profile' },
            },
            {
              path: 'profile',
              name: 'My Profile Edit Profile',
              component: () => import(/* webpackChunkName: "profile-edit-profile" */ '../components/pages/ProfileEditProfile.vue'),
            },
            {
              path: 'account',
              name: 'My Profile Edit Account',
              component: () => import(/* webpackChunkName: "profile-edit-account" */ '../components/pages/ProfileEditAccount.vue'),
            },
          ],
        },
        {
          path: 'dashboard',
          name: 'My Dashboard',
          beforeEnter: [requiresAuth],
          meta: {
            title: {
              en: 'Dashboard',
              ru: 'Дашборд',
            },
          },
          component: () => import(/* webpackChunkName: "profile-dashboard" */ '../components/pages/UserDashboard.vue'),
        },
      ],
    }
  };
}
